import { useMemo } from "react";
import toast from "react-hot-toast";
import { useCreatePackageCheckoutMutation } from "@/graphql/mutations/createPackageCheckout.graphql.types";
import useAction from "@/hooks/misc/useAction";
import {
  EventType,
  OnlineStoreSource,
  OrderType,
  useOnlineStoreMixpanelEvents,
} from "@/hooks/onlineStore/useOnlineStoreMixpanelEvents";
import usePersistedStore from "@/store/usePersistedStore";
import useOnlineStorePackagesStore from "@/store/views/onlineStore/onlineStorePackagesStore";
import useErrorLogger from "@/utils/useErrorLogger";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";

export type GiftRecipientInput = {
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  recipientPhone: string;
};

export default function useCreatePackageCheckout(
  medspaId: string,
  medspaPackages: MedspaPackage[]
) {
  const { trackEvent } = useOnlineStoreMixpanelEvents(medspaId);
  const logError = useErrorLogger();
  const [createPackageCheckout] = useCreatePackageCheckoutMutation();

  const store = useOnlineStorePackagesStore(medspaId);

  const selectedPackages = usePersistedStore(
    store,
    (state) => state.packagesQuantity
  );

  const source = usePersistedStore(store, (state) => state.getPurchaseSource());

  const mappedPackages = useMemo(() => {
    if (!selectedPackages || Object.keys(selectedPackages).length === 0)
      return [];

    return Object.entries(selectedPackages)
      .map(([packageId, quantity]) => {
        const p = medspaPackages.find((p) => p.id === packageId);

        // Edge case when package is persisted in cart but medspa decided to remove it
        if (!p) return null;

        return {
          packageId,
          quantity,
        };
      })
      .filter(Boolean);
  }, [selectedPackages, medspaPackages]);

  const buyPackage = async (recipientData?: GiftRecipientInput) => {
    try {
      const { data } = await toast.promise(
        createPackageCheckout({
          variables: {
            packages: mappedPackages,
            ...(recipientData ? recipientData : {}),
            source,
          },
        }),
        {
          loading: "Redirecting to secure checkout page...",
          success: "Created checkout",
          error: "Failed to create checkout. Please reach out to support.",
        }
      );
      trackEvent(EventType.CHECKOUT_INITIATED, {
        orderType: recipientData ? OrderType.STANDARD_GIFT : OrderType.STANDARD,
        source: source || OnlineStoreSource.ONLINE_STORE,
      });

      // redirect to checkout page
      window.location.href = data.createPackageCheckout.url;
    } catch (errors) {
      logError(errors);
    }
  };

  const { handleAction: goToCheckout, isLoading } = useAction(buyPackage);

  return { goToCheckout, isLoading };
}
